import { morphism } from 'morphism'
import { Beneficiary, BeneficiaryType } from 'types/finance'
import { currencyUtils } from '../../../utils'
import { getInvoiceTypeDisplayName } from '../invoice/utils'
import {
  TCreditNoteRequest,
  TCustomerAccountActivity,
  TOpenInvoiceSummary,
  TPaymentAllocation,
  TReconBeneficiaryRule,
  TReconInvoice,
  TUpdateBeneficiariesResponse,
} from './types'

const { toCents, fromCents } = currencyUtils

/**
 * Every amount on the wire is in rands; every amount in the store is in cents. These
 * schemas are the only place the two representations meet, so the `T*` types on either
 * side of a transformer describe the same field set with different units.
 */

type TWireBeneficiaryRule = TReconBeneficiaryRule

const toCentsAmount = (amount: number | undefined) => toCents(amount).getAmount()

const beneficiaryRuleToCents = (b: TWireBeneficiaryRule): TReconBeneficiaryRule =>
  ({
    ...b,
    beneficiary: {
      ...b.beneficiary,
      value: {
        ...b.beneficiary.value,
        amount: toCentsAmount(b.beneficiary.value.amount),
      },
    },
    balance: toCentsAmount(b.balance),
  } as TReconBeneficiaryRule)

const invoiceResponseSchema = {
  id: 'id',
  agencyId: 'agencyId',
  invoiceType: ({ invoiceType }: TReconInvoice) => getInvoiceTypeDisplayName(invoiceType),
  accountId: 'accountId',
  customerId: 'customerId',
  customerName: 'customerName',
  customerAvailableFunds: ({ customerAvailableFunds }: TReconInvoice) => toCentsAmount(customerAvailableFunds),
  dueDate: 'dueDate',
  grossAmount: ({ grossAmount }: TReconInvoice) => toCentsAmount(grossAmount),
  vatAmount: ({ vatAmount }: TReconInvoice) => toCentsAmount(vatAmount),
  netAmount: ({ netAmount }: TReconInvoice) => toCentsAmount(netAmount),
  balance: ({ balance }: TReconInvoice) => toCentsAmount(balance),
  portfolioId: 'portfolioId',
  beneficiaries: ({ beneficiaries }: TReconInvoice) => beneficiaries.map(beneficiaryRuleToCents),
  payments: 'payments',
  creditNotes: ({ creditNotes = [] }: TReconInvoice) =>
    creditNotes.map(cn => ({
      ...cn,
      amount: toCentsAmount(cn.amount),
      beneficiaries: cn.beneficiaries.map(b => ({
        ...b,
        amount: toCentsAmount(b.amount),
      })),
    })),
  invoiceStatus: 'invoiceStatus',
  reconStatus: 'reconStatus',
  invoiceNumber: 'invoiceNumber',
  property: 'property',
  mainText: 'mainText',
  secondaryText: 'secondaryText',
  metaData: 'metaData',
  lastNudgedAt: 'lastNudgedAt',
  description: 'description',
}

/**
 * `morphism` maps an array source element-wise, so this is overloaded to mirror what the
 * caller passed in: the single-invoice endpoints hand it an object, the list endpoints an
 * array.
 */
export function transformInvoiceResponse(source: TReconInvoice): TReconInvoice
export function transformInvoiceResponse(source: TReconInvoice[]): TReconInvoice[]
export function transformInvoiceResponse(source: TReconInvoice | TReconInvoice[]): TReconInvoice | TReconInvoice[]
export function transformInvoiceResponse(source: TReconInvoice | TReconInvoice[]): TReconInvoice | TReconInvoice[] {
  return morphism(invoiceResponseSchema, source as never) as unknown as TReconInvoice | TReconInvoice[]
}

const creditNoteRequestSchema = {
  amount: ({ amount }: TCreditNoteRequest) => fromCents(amount),
  reason: 'reason',
  description: 'description',
  userUnderstandsRisk: 'userUnderstandsRisk',
  beneficiaries: ({ beneficiaries }: TCreditNoteRequest) =>
    beneficiaries.map(b => ({
      beneficiaryId: b.beneficiaryId,
      amount: fromCents(b.amount),
    })),
}

export const transformCreditNoteRequest = (source: TCreditNoteRequest): TCreditNoteRequest =>
  morphism(creditNoteRequestSchema, source) as unknown as TCreditNoteRequest

const customerAccountActivitySchema = {
  customerName: 'customerName',
  availableFunds: ({ availableFunds }: TCustomerAccountActivity) => toCentsAmount(availableFunds),
  amountOutstanding: ({ amountOutstanding }: TCustomerAccountActivity) => toCentsAmount(amountOutstanding),
  openInvoices: ({ openInvoices }: TCustomerAccountActivity) =>
    openInvoices.map(invoice => ({
      ...invoice,
      balance: toCentsAmount(invoice.balance),
    })),
  recentActivity: ({ recentActivity }: TCustomerAccountActivity) =>
    recentActivity.map(ra => ({
      paymentReference: ra.paymentReference,
      sourceId: ra.sourceId,
      sourceType: ra.sourceType,
      customerId: ra.customerId,
      accountId: ra.accountId,
      date: ra.date,
      description: ra.description,
      isCredit: ra.isCredit,
      amount: toCentsAmount(ra.amount),
    })),
}

export const transformCustomerAccountActivity = (source: TCustomerAccountActivity): TCustomerAccountActivity =>
  morphism(customerAccountActivitySchema, source) as unknown as TCustomerAccountActivity

const activeInvoiceSchema = {
  invoiceId: 'invoiceId',
  invoiceType: ({ invoiceType }: { invoiceType: string }) => getInvoiceTypeDisplayName(invoiceType),
  description: 'description',
  balance: ({ balance }: TOpenInvoiceSummary) => toCentsAmount(balance),
  invoiceStatus: 'invoiceStatus',
}

export const transformActiveInvoices = (source: TOpenInvoiceSummary[]): TOpenInvoiceSummary[] =>
  morphism(activeInvoiceSchema, source as never) as unknown as TOpenInvoiceSummary[]

const createPaymentRequestSchema = {
  beneficiaryId: 'beneficiaryId',
  amount: ({ amount }: TPaymentAllocation) => fromCents(amount),
}

/**
 * Recon discards payments of zero or less, so a row with no amount contributes nothing.
 * It still has to be dropped here: a beneficiary that was persisted without an id (Beneficiary.id
 * is optional server side, and older invoices have rows without one) serialises as
 * `{ amount: null }`, which fails Seq[Payment] deserialisation and rejects the whole allocation.
 */
const isPayable = (payment: TPaymentAllocation) => Number(payment?.amount) > 0

export const transformCreatePaymentRequest = (
  source: TPaymentAllocation[] | TPaymentAllocation,
): TPaymentAllocation[] | TPaymentAllocation =>
  morphism(
    createPaymentRequestSchema,
    (Array.isArray(source) ? source.filter(isPayable) : source) as never,
  ) as unknown as TPaymentAllocation[] | TPaymentAllocation

const updateBeneficiariesRequestSchema = {
  type: 'beneficiary.type',
  value: (b: Beneficiary) => {
    const { beneficiary } = b
    switch (beneficiary.type) {
      case BeneficiaryType.EasyPayBeneficiary:
        return {
          beneficiaryTag: beneficiary.value.beneficiaryTag,
          easyPayReference: beneficiary.value.easyPayReference,
          vat: beneficiary.value.vat || false,
          amount: fromCents(beneficiary.value.amount),
          id: beneficiary.value.id,
        }
      case BeneficiaryType.DepositBeneficiary:
        return {
          partyId: beneficiary.value.partyId,
          beneficiaryTag: beneficiary.value.beneficiaryTag,
          accountId: beneficiary.value.accountId,
          reference: beneficiary.value.reference,
          amount: fromCents(beneficiary.value.amount),
          transfer: beneficiary.value.transfer,
          vat: beneficiary.value.vat,
          id: beneficiary.value.id,
        }
      default:
        return {
          partyId: beneficiary.value.partyId,
          partyTag: beneficiary.value.partyTag,
          reference: beneficiary.value.reference,
          amount: fromCents(beneficiary.value.amount),
          vat: beneficiary.value.vat,
          id: beneficiary.value.id,
        }
    }
  },
}

export const transformUpdateBeneficiariesRequest = (source: Beneficiary[]): unknown =>
  morphism(updateBeneficiariesRequestSchema, source as never)

const updateBeneficiariesResponseSchema = {
  status: 'status',
  invoices: ({ invoices }: TUpdateBeneficiariesResponse) => invoices?.map(inv => transformInvoiceResponse(inv)),
  beneficiaries: ({ beneficiaries }: TUpdateBeneficiariesResponse) => beneficiaries.map(beneficiaryRuleToCents),
}

export const transformUpdateBeneficiariesResponse = (
  source: TUpdateBeneficiariesResponse,
): TUpdateBeneficiariesResponse =>
  morphism(updateBeneficiariesResponseSchema, source) as unknown as TUpdateBeneficiariesResponse
